import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EmailPDFModal = ({ 
  isOpen, 
  onClose, 
  onSend, 
  loading, 
  success, 
  error,
  sentEmail 
}) => {
  const [emailAddress, setEmailAddress] = useState('');
  const [emailError, setEmailError] = useState('');

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleEmailChange = (e) => {
    const email = e?.target?.value;
    setEmailAddress(email);
    
    if (email && !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleSend = () => {
    if (!emailAddress?.trim()) {
      setEmailError('Email address is required');
      return;
    }

    if (!validateEmail(emailAddress)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    onSend(emailAddress?.trim());
  };

  const handleClose = () => {
    setEmailAddress('');
    setEmailError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-organic max-w-md w-full shadow-organic-lg">
        {!success ? (
          // Email Input Form
          <>
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="font-heading font-heading-bold text-lg text-card-foreground">
                Email Trip PDF
              </h3>
              <button
                onClick={handleClose}
                className="p-1 hover:bg-muted rounded-organic transition-colors"
              >
                <Icon name="X" size={20} color="var(--color-muted-foreground)" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Email Input */}
              <div>
                <label className="block text-sm font-body font-body-medium text-card-foreground mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={emailAddress}
                  onChange={handleEmailChange}
                  placeholder="Enter your email address"
                  className={`w-full px-3 py-2 border rounded-organic focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                    emailError ? 'border-destructive' : 'border-border'
                  }`}
                  disabled={loading}
                />
                {emailError && (
                  <p className="text-xs text-destructive mt-1">{emailError}</p>
                )}
              </div>

              {/* PDF Preview Info */}
              <div className="bg-muted/30 rounded-organic p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="FileText" size={16} color="var(--color-primary)" />
                  <span className="text-sm font-body font-body-medium text-card-foreground">
                    PDF will include:
                  </span>
                </div>
                <ul className="text-xs text-muted-foreground space-y-1 ml-6">
                  <li>• Complete day-by-day itinerary</li>
                  <li>• Park information and maps</li>
                  <li>• Activity recommendations</li>
                  <li>• Weather forecast and packing tips</li>
                  <li>• Accommodation and dining details</li>
                </ul>
              </div>

              {/* Error Display */}
              {error && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-organic">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-2">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={loading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleSend}
                  disabled={loading || !emailAddress?.trim() || !!emailError}
                  iconName={loading ? "Loader" : "Mail"}
                  iconPosition="left"
                  className="flex-1"
                >
                  {loading ? 'Sending...' : 'Send PDF'}
                </Button>
              </div>
            </div>
          </>
        ) : (
          // Success State
          <div className="p-6">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-success rounded-organic flex items-center justify-center">
                  <Icon name="CheckCircle" size={32} color="white" />
                </div>
              </div>
              <h3 className="font-heading font-heading-bold text-xl text-card-foreground mb-2">
                Successfully Sent!
              </h3>
              <p className="text-muted-foreground mb-6">
                Check your inbox at <strong>{sentEmail}</strong> for your trip PDF.
              </p>
              <div className="bg-muted/30 rounded-organic p-3 mb-4">
                <p className="text-xs text-muted-foreground">
                  💡 <strong>Tip:</strong> Check your spam folder if you do not see the email within a few minutes.
                </p>
              </div>
              <Button
                variant="default"
                onClick={handleClose}
                className="w-full"
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailPDFModal;